import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { IdType, ReloadFormRequest } from 'epic/common-form/ReloadFormRequest';
import type { OrdersBasketPersistedForm } from 'models/forms/OrdersBasketPersistedForm';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ProcessingChain } from 'models/ProcessingChain';
import { ProductVariation, ProductVariationBase } from 'models/productsReferential';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { DataById } from 'state/formData/formData.model';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { ImportFormRequest } from 'utils/import/import.models';

import type { OrdersBasketFormMetadata, OrdersBasketQuoteResponse, OrdersBasketTradeResponse } from './models';

export type OrdersBasketFormsActions =
  | AddToOrdersBasket
  | NewOrdersBasketFormRequested
  | OrdersBasketFormInitialized
  | OrdersBasketFormInitializationFailed
  | OrdersBasketFormSelectionToggled
  | OrdersBasketAllFormsSelected
  | OrdersBasketAllFormsUnselected
  | OrdersBasketFormDeletionRequested
  | OrdersBasketFormDeleted
  | OrdersBasketFormValueChanged
  | OrdersBasketFormUpdateRequested
  | OrdersBasketFormUpdated
  | OrdersBasketFormUpdateFailed
  | OrdersBasketFormQuoteRequested
  | OrdersBasketFormQuoteCanceled
  | OrdersBasketFormQuoteAccepted
  | OrdersBasketFormQuoted
  | OrdersBasketFormQuoteRejected
  | OrdersBasketFormQuoteFailed
  | OrdersBasketFormQuoteTimeout
  | OrdersBasketFormTradeAuthGranted
  | OrdersBasketFormTradeAuthRejected
  | OrdersBasketFormTradeAuthFailed
  | OrdersBasketFormTradeAuthTimeout
  | OrdersBasketFormTradeRequested
  | OrdersBasketFormTradeAccepted
  | OrdersBasketFormTraded
  | OrdersBasketFormTradeFailed
  | OrdersBasketFormTradeRejected
  | OrdersBasketFormTradeTimeout
  | OrdersBasketFormTradedWithWarning
  | OrdersBasketFormMetadataPersistenceRequested
  | OrdersBasketFormMetadataPersisted
  | OrdersBasketFormMetadataPersistenceFailed
  | OrdersBasketFormMetadataRemovalRequested
  | OrdersBasketFormMetadataRemoved
  | OrdersBasketFormMetadataRemovalFailed
  | OrdersBasketPersistedFormFetchRequested
  | OrdersBasketPersistedFormFetched
  | OrdersBasketPersistedFormFetchFailed
  | OrdersBasketImportRequested
  | OrdersBasketImportReceived
  | OrdersBasketImportFailed
  | OrdersBasketImportWrongProductReceived
  | OrdersBasketInitFormRequested
  | OrdersBasketInitFormFailed
  | OrdersBasketAllBookingResultsReceived;

export function addToOrdersBasket(
  quoteId: string,
  wrapper: string,
  productType: string,
  productSubType: string,
  tabName: string,
  processingChain?: ProcessingChain,
  pricerRfqId?: string,
  productId?: string,
) {
  return {
    type: 'ADD_TO_ORDERS_BASKET' as const,
    quoteId,
    wrapper,
    productType,
    productSubType,
    tabName,
    processingChain,
    pricerRfqId,
    productId,
  };
}
export type AddToOrdersBasket = ReturnType<typeof addToOrdersBasket>;

export function newOrdersBasketFormRequested(
  id: string,
  idType: IdType,
  tradeDate: string,
  temporaryNegotiationId: string,
  tabId: string,
  wrapper: string,
  productType: string,
  productSubType: string,
  processingChain?: ProcessingChain,
  pricerRfqId?: string,
) {
  return {
    type: 'NEW_ORDERS_BASKET_FORM_REQUESTED' as const,
    id,
    idType,
    tradeDate,
    temporaryNegotiationId,
    tabId,
    wrapper,
    productType,
    productSubType,
    processingChain,
    pricerRfqId,
  };
}
export type NewOrdersBasketFormRequested = ReturnType<typeof newOrdersBasketFormRequested>;

export function ordersBasketFormInitialized(tabId: string, temporaryNegotiationId: string, form: StaticFormResult) {
  return { type: 'ORDERS_BASKET_BATCH_WORKSPACE_FORM_INITIALIZED' as const, tabId, temporaryNegotiationId, form };
}
export type OrdersBasketFormInitialized = ReturnType<typeof ordersBasketFormInitialized>;

export function ordersBasketFormInitializationFailed(temporaryNegotiationId: string) {
  return { type: 'ORDERS_BASKET_BATCH_WORKSPACE_FORM_INITIALIZATION_FAILED' as const, temporaryNegotiationId };
}
export type OrdersBasketFormInitializationFailed = ReturnType<typeof ordersBasketFormInitializationFailed>;

export function ordersBasketFormSelectionToggled(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_SELECTION_TOGGLED' as const, formId };
}
export type OrdersBasketFormSelectionToggled = ReturnType<typeof ordersBasketFormSelectionToggled>;

export function ordersBasketAllFormsSelected(productVariation: ProductVariationBase) {
  return { type: 'ORDERS_BASKET_ALL_FORMS_SELECTED' as const, productVariation };
}
export type OrdersBasketAllFormsSelected = ReturnType<typeof ordersBasketAllFormsSelected>;

export function ordersBasketAllFormsUnselected(productVariation: ProductVariationBase) {
  return { type: 'ORDERS_BASKET_ALL_FORMS_UNSELECTED' as const, productVariation };
}
export type OrdersBasketAllFormsUnselected = ReturnType<typeof ordersBasketAllFormsUnselected>;

export function ordersBasketFormDeletionRequested(tabId: string, formId: string) {
  return { type: 'ORDERS_BASKET_FORM_DELETION_REQUESTED' as const, tabId, formId };
}
export type OrdersBasketFormDeletionRequested = ReturnType<typeof ordersBasketFormDeletionRequested>;

export function ordersBasketFormDeleted(tabId: string, formId: string) {
  return { type: 'ORDERS_BASKET_FORM_DELETED' as const, tabId, formId };
}
export type OrdersBasketFormDeleted = ReturnType<typeof ordersBasketFormDeleted>;

export function ordersBasketFormValueChanged(formId: string, fieldId: string, value: unknown) {
  return { type: 'ORDERS_BASKET_FORM_VALUE_CHANGED' as const, formId, fieldId, value };
}
export type OrdersBasketFormValueChanged = ReturnType<typeof ordersBasketFormValueChanged>;

export function ordersBasketFormUpdateRequested(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_UPDATE_REQUESTED' as const, formId };
}
export type OrdersBasketFormUpdateRequested = ReturnType<typeof ordersBasketFormUpdateRequested>;

export function ordersBasketFormUpdated(formId: string, updatedParameters: DataById, result: StaticFormResult) {
  return { type: 'ORDERS_BASKET_FORM_UPDATED' as const, formId, updatedParameters, result };
}
export type OrdersBasketFormUpdated = ReturnType<typeof ordersBasketFormUpdated>;

export function ordersBasketFormUpdateFailed(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_UPDATE_FAILED' as const, formId };
}
export type OrdersBasketFormUpdateFailed = ReturnType<typeof ordersBasketFormUpdateFailed>;

export function ordersBasketFormQuoteRequested(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_REQUESTED' as const, formId };
}
export type OrdersBasketFormQuoteRequested = ReturnType<typeof ordersBasketFormQuoteRequested>;

export function ordersBasketFormQuoteCanceled(formId: string, quoteId?: string) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_CANCELED' as const, formId, quoteId };
}
export type OrdersBasketFormQuoteCanceled = ReturnType<typeof ordersBasketFormQuoteCanceled>;

export function ordersBasketFormQuoteAccepted(formId: string, quoteAck: QuoteAck) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_ACCEPTED' as const, formId, quoteAck };
}
export type OrdersBasketFormQuoteAccepted = ReturnType<typeof ordersBasketFormQuoteAccepted>;

export function ordersBasketFormQuoted(
  formId: string,
  quoteResponse: OrdersBasketQuoteResponse,
  isPendingTradeAuth: boolean,
) {
  return { type: 'ORDERS_BASKET_FORM_QUOTED' as const, formId, quoteResponse, isPendingTradeAuth };
}
export type OrdersBasketFormQuoted = ReturnType<typeof ordersBasketFormQuoted>;

export function ordersBasketFormQuoteRejected(formId: string, quoteResponse: OrdersBasketQuoteResponse) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_REJECTED' as const, formId, quoteResponse };
}
export type OrdersBasketFormQuoteRejected = ReturnType<typeof ordersBasketFormQuoteRejected>;

export function ordersBasketFormQuoteFailed(formId: string, newVersion?: number) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_FAILED' as const, formId, newVersion };
}
export type OrdersBasketFormQuoteFailed = ReturnType<typeof ordersBasketFormQuoteFailed>;

export function ordersBasketFormQuoteTimeout(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_QUOTE_TIMEOUT' as const, formId };
}
export type OrdersBasketFormQuoteTimeout = ReturnType<typeof ordersBasketFormQuoteTimeout>;

export function ordersBasketFormTradeAuthGranted(
  formId: string,
  tradeAuthorization: NextTradeAuthorizationResult,
  isPendingQuote: boolean,
) {
  return {
    type: 'ORDERS_BASKET_FORM_TRADE_AUTH_GRANTED' as const,
    formId,
    tradeAuthorization,
    isPendingQuote,
  };
}
export type OrdersBasketFormTradeAuthGranted = ReturnType<typeof ordersBasketFormTradeAuthGranted>;

export function ordersBasketFormTradeAuthRejected(formId: string, tradeAuthorization: NextTradeAuthorizationResult) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_AUTH_REJECTED' as const, formId, tradeAuthorization };
}
export type OrdersBasketFormTradeAuthRejected = ReturnType<typeof ordersBasketFormTradeAuthRejected>;

export function ordersBasketFormTradeAuthFailed(formId: string, error: ApiErrorMessage) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_AUTH_FAILED' as const, formId, error };
}
export type OrdersBasketFormTradeAuthFailed = ReturnType<typeof ordersBasketFormTradeAuthFailed>;

export function ordersBasketFormTradeAuthTimeout(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_AUTH_TIMEOUT' as const, formId };
}
export type OrdersBasketFormTradeAuthTimeout = ReturnType<typeof ordersBasketFormTradeAuthTimeout>;

export function ordersBasketFormTradeRequested(formId: string, quoteId: string, product: ProductVariation) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_REQUESTED' as const, formId, quoteId, product };
}
export type OrdersBasketFormTradeRequested = ReturnType<typeof ordersBasketFormTradeRequested>;

export function ordersBasketFormTradeAccepted(formId: string, quoteId: string) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_ACCEPTED' as const, formId, quoteId };
}
export type OrdersBasketFormTradeAccepted = ReturnType<typeof ordersBasketFormTradeAccepted>;

export function ordersBasketFormTraded(formId: string, tradeResponse: OrdersBasketTradeResponse, isBooking: boolean) {
  return { type: 'ORDERS_BASKET_FORM_TRADED' as const, formId, tradeResponse, isBooking };
}
export type OrdersBasketFormTraded = ReturnType<typeof ordersBasketFormTraded>;

export function ordersBasketFormTradeFailed(formId: string, error: ApiErrorMessage) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_FAILED' as const, formId, error };
}
export type OrdersBasketFormTradeFailed = ReturnType<typeof ordersBasketFormTradeFailed>;

export function ordersBasketFormTradeTimeout(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_TIMEOUT' as const, formId };
}
export type OrdersBasketFormTradeTimeout = ReturnType<typeof ordersBasketFormTradeTimeout>;

export function ordersBasketFormTradeRejected(formId: string, tradeResponse: OrdersBasketTradeResponse) {
  return { type: 'ORDERS_BASKET_FORM_TRADE_REJECTED' as const, formId, tradeResponse };
}
export type OrdersBasketFormTradeRejected = ReturnType<typeof ordersBasketFormTradeRejected>;

export function ordersBasketFormTradedWithWarning(formId: string, tradeResponse: OrdersBasketTradeResponse) {
  return { type: 'ORDERS_BASKET_FORM_TRADED_WITH_WARNING' as const, formId, tradeResponse };
}
export type OrdersBasketFormTradedWithWarning = ReturnType<typeof ordersBasketFormTradedWithWarning>;

export function ordersBasketFormMetadataPersistenceRequested(formId: string, metadata: OrdersBasketFormMetadata) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_PERSISTENCE_REQUESTED' as const, formId, metadata };
}
export type OrdersBasketFormMetadataPersistenceRequested = ReturnType<
  typeof ordersBasketFormMetadataPersistenceRequested
>;

export function ordersBasketFormMetadataPersisted(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_PERSISTED' as const, formId };
}
export type OrdersBasketFormMetadataPersisted = ReturnType<typeof ordersBasketFormMetadataPersisted>;

export function ordersBasketFormMetadataPersistenceFailed(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_PERSISTENCE_FAILED' as const, formId };
}
export type OrdersBasketFormMetadataPersistenceFailed = ReturnType<typeof ordersBasketFormMetadataPersistenceFailed>;

export function ordersBasketFormMetadataRemovalRequested(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_REMOVAL_REQUESTED' as const, formId };
}
export type OrdersBasketFormMetadataRemovalRequested = ReturnType<typeof ordersBasketFormMetadataRemovalRequested>;

export function ordersBasketFormMetadataRemoved(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_REMOVED' as const, formId };
}
export type OrdersBasketFormMetadataRemoved = ReturnType<typeof ordersBasketFormMetadataRemoved>;

export function ordersBasketFormMetadataRemovalFailed(formId: string) {
  return { type: 'ORDERS_BASKET_FORM_METADATA_REMOVAL_FAILED' as const, formId };
}
export type OrdersBasketFormMetadataRemovalFailed = ReturnType<typeof ordersBasketFormMetadataRemovalFailed>;

export function ordersBasketPersistedFormFetchRequested(
  formId: string,
  productType: string,
  productSubType: string,
  wrapper?: string,
) {
  return {
    type: 'ORDERS_BASKET_PERSISTED_FORM_FETCH_REQUESTED' as const,
    formId,
    productType,
    productSubType,
    wrapper,
  };
}
export type OrdersBasketPersistedFormFetchRequested = ReturnType<typeof ordersBasketPersistedFormFetchRequested>;

export function ordersBasketPersistedFormFetched(formId: string, form: OrdersBasketPersistedForm) {
  return { type: 'ORDERS_BASKET_PERSISTED_FORM_FETCHED' as const, formId, form };
}
export type OrdersBasketPersistedFormFetched = ReturnType<typeof ordersBasketPersistedFormFetched>;

export function ordersBasketPersistedFormFetchFailed(formId: string) {
  return { type: 'ORDERS_BASKET_PERSISTED_FORM_FETCH_FAILED' as const, formId };
}
export type OrdersBasketPersistedFormFetchFailed = ReturnType<typeof ordersBasketPersistedFormFetchFailed>;

export function ordersBasketImportRequested(
  request: ReloadFormRequest,
  productVariation: ProductVariationBase,
  tabId: string,
  temporaryNegotiationId: string,
) {
  return { type: 'ORDERS_BASKET_IMPORT_REQUESTED' as const, request, productVariation, tabId, temporaryNegotiationId };
}
export type OrdersBasketImportRequested = ReturnType<typeof ordersBasketImportRequested>;

export function ordersBasketImportReceived(
  tabId: string,
  temporaryNegotiationId: string,
  negotiationId: string,
  form: StaticFormResult,
) {
  return { type: 'ORDERS_BASKET_IMPORT_RECEIVED' as const, tabId, temporaryNegotiationId, negotiationId, form };
}
export type OrdersBasketImportReceived = ReturnType<typeof ordersBasketImportReceived>;

export function ordersBasketImportFailed(tabId: string, temporaryNegotiationId: string) {
  return { type: 'ORDERS_BASKET_IMPORT_FAILED' as const, tabId, temporaryNegotiationId };
}
export type OrdersBasketImportFailed = ReturnType<typeof ordersBasketImportFailed>;

export function ordersBasketImportWrongProductReceived(tabId: string, temporaryNegotiationId: string) {
  return { type: 'ORDERS_BASKET_IMPORT_WRONG_PRODUCT_RECEIVED' as const, tabId, temporaryNegotiationId };
}
export type OrdersBasketImportWrongProductReceived = ReturnType<typeof ordersBasketImportWrongProductReceived>;

export function ordersBasketInitFormRequested(
  tabId: string,
  temporaryNegotiationId: string,
  request: ImportFormRequest,
) {
  return { type: 'ORDERS_BASKET_INIT_FORM_REQUESTED' as const, tabId, temporaryNegotiationId, request };
}
export type OrdersBasketInitFormRequested = ReturnType<typeof ordersBasketInitFormRequested>;

export function ordersBasketInitFormFailed(tabId: string, temporaryNegotiationId: string) {
  return { type: 'ORDERS_BASKET_INIT_FORM_FAILED' as const, tabId, temporaryNegotiationId };
}
export type OrdersBasketInitFormFailed = ReturnType<typeof ordersBasketInitFormFailed>;

export function ordersBasketAllBookingResultsReceived(formId: string) {
  return {
    type: 'ORDERS_BASKET_ALL_BOOKING_RESULTS_RECEIVED' as const,
    formId,
  };
}
export type OrdersBasketAllBookingResultsReceived = ReturnType<typeof ordersBasketAllBookingResultsReceived>;
